import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { colors, fontFamily, fontSizes, fontWeights, layout, radii } from '../../theme/tokens';
import { SITE } from '../../config/siteContent';
import { LOGO_IMAGES } from '../../config/imagePaths';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import ApiService from '../../services/apiService';
import { Container, Icon } from '../ui';
import PublicHeader, { isPublicBrowsePath } from './PublicHeader';
import { SCHOOL_ORDERS_LABEL } from '../../utils/schoolOrders';

const NAV = [
  { id: 'home', label: 'Home', to: '/' },
  { id: 'school', label: 'School Zone', to: '/search' },
  { id: 'orders', label: 'My Orders', to: '/orders' },
];

const getActiveId = (path) => {
  if (path === '/search' || path.startsWith('/school/') || path.startsWith('/grade/')) return 'school';
  if (path === '/orders' || path.startsWith('/orders/')) return 'orders';
  if (path === '/school-orders') return 'schoolOrders';
  if (path === '/') return 'home';
  return null;
};

const SiteHeader = () => {
  const { isLoggedIn, logout, staffAccess } = useAuth();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Close mobile menu on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Cart count: same read CartPage uses (ApiService.getCart), refreshed on route change.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await ApiService.getCart();
        if (cancelled) return;
        if (result && result.success && result.data) {
          setCartCount((result.data.items || []).length);
        } else {
          setCartCount(0);
        }
      } catch (e) {
        if (!cancelled) setCartCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, pathname]);

  if (!isLoggedIn) return isPublicBrowsePath(pathname) ? <PublicHeader /> : null;

  const activeId = getActiveId(pathname);
  const nav = staffAccess?.hasAccess
    ? NAV.concat({ id: 'schoolOrders', label: SCHOOL_ORDERS_LABEL, to: '/school-orders' })
    : NAV;
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const iconBtn = {
    position: 'relative', width: 48, height: 48, borderRadius: radii.button, border: '1px solid ' + colors.border,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.navy, background: colors.white,
    cursor: 'pointer', padding: 0, textDecoration: 'none', flexShrink: 0,
  };
  const barHeight = isMobile ? layout.headerHeightMobile : layout.headerHeight;
  const logoSize = isMobile ? 44 : 64;

  const cartLink = (
    <Link to="/cart" aria-label={cartCount > 0 ? 'Cart, ' + cartCount + ' items' : 'Cart'} style={iconBtn}>
      <Icon name="cart" size={24} />
      {cartCount > 0 && (
        <span
          style={{
            position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, padding: '0 5px', boxSizing: 'border-box',
            borderRadius: 10, background: colors.navy, color: colors.white, fontSize: 12, fontWeight: fontWeights.semibold,
            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid ' + colors.white,
          }}
        >
          {cartCount}
        </span>
      )}
    </Link>
  );

  return (
    <header
      style={{
        width: '100%', background: colors.white, fontFamily, borderBottom: '1px solid ' + colors.border,
        position: 'relative', zIndex: 50,
      }}
    >
      {SITE.promoBar.enabled && SITE.promoBar.text && (
        <div
          style={{
            background: colors.navy, color: colors.white, padding: '9px 16px', display: 'flex', flexWrap: 'wrap',
            justifyContent: 'center', alignItems: 'center', gap: 12, fontSize: fontSizes.small, fontWeight: fontWeights.medium,
            textAlign: 'center',
          }}
        >
          <span>{SITE.promoBar.text}</span>
          <Link to={SITE.promoBar.ctaPath} style={{ color: colors.white, fontWeight: fontWeights.semibold, textDecoration: 'underline' }}>
            {SITE.promoBar.ctaLabel}
          </Link>
        </div>
      )}
      <Container style={{ height: barHeight, display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 40 }}>
        <Link to="/" aria-label="VidyarthiKart home" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src={LOGO_IMAGES.LOCKUP}
            alt="VidyarthiKart - Official book partner"
            style={{ height: logoSize, width: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </Link>

        {isMobile ? (
          <>
            <div style={{ flexGrow: 1 }} />
            {cartLink}
            <button
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              style={iconBtn}
            >
              <Icon name={menuOpen ? 'close' : 'menu'} size={24} />
            </button>
          </>
        ) : (
          <>
            <nav aria-label="Main" style={{ display: 'flex', alignItems: 'stretch', gap: 8, height: barHeight, flexGrow: 1 }}>
              {nav.map((n) => {
                const on = n.id === activeId;
                return (
                  <Link
                    key={n.id}
                    to={n.to}
                    aria-current={on ? 'page' : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: fontSizes.body,
                      fontWeight: fontWeights.medium, textDecoration: 'none', boxSizing: 'border-box',
                      color: on ? colors.navy : colors.ink,
                      borderBottom: '3px solid ' + (on ? colors.navy : 'transparent'),
                    }}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
            {cartLink}
            <Link
              to="/profile"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, height: 48, padding: '0 14px 0 6px', borderRadius: 24,
                border: '1px solid ' + colors.border, background: colors.white, fontSize: fontSizes.body - 1,
                fontWeight: fontWeights.medium, color: colors.ink, textDecoration: 'none', flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 36, height: 36, borderRadius: 18, background: colors.softBlue, color: colors.navy,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="user" size={20} />
              </span>
              <span>My account</span>
              <Icon name="chevronDown" size={16} strokeWidth={2} />
            </Link>
          </>
        )}
      </Container>

      {isMobile && menuOpen && (
        <nav
          aria-label="Mobile"
          style={{
            borderTop: '1px solid ' + colors.divider, background: colors.white, padding: '8px ' + layout.gutterMobile + 'px 16px',
            display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 'calc(100dvh - ' + barHeight + 'px)', overflowY: 'auto',
          }}
        >
          {nav.concat([{ id: 'account', label: 'My account', to: '/profile' }]).map((n) => {
            const on = n.id === activeId;
            return (
              <Link
                key={n.id}
                to={n.to}
                aria-current={on ? 'page' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', minHeight: 52, padding: '0 12px', borderRadius: radii.input,
                  fontSize: fontSizes.body, fontWeight: fontWeights.semibold, textDecoration: 'none',
                  background: on ? colors.mist : 'transparent', color: on ? colors.navy : colors.ink,
                }}
              >
                {n.label}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '0 12px', borderRadius: radii.input,
              border: 0, borderTop: '1px solid ' + colors.divider, background: 'transparent', cursor: 'pointer',
              fontFamily, fontSize: fontSizes.body, fontWeight: fontWeights.semibold, color: colors.dangerStrong,
            }}
          >
            <Icon name="logout" size={20} />
            Logout
          </button>
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
